<template>
    <b-modal id="categoryModal" @hide="resetFields" @show="init">
        <template v-slot:modal-title>
            <div class="d-flex align-items-center">
                <font-awesome-icon class="color-brand mr-2" :icon="['fal', 'plus']" />
                <span class="color-brand modal-title">{{
                    isEdit ? $t('admin.category_modal_edit_title') : $t('admin.category_modal_create_title')
                }}</span>
            </div>
        </template>
        <template>
            <b-alert class="mt-2" variant="info" show>
                <font-awesome-icon :icon="['far', 'square-info']" size="xl" class="mr-2"></font-awesome-icon>
                <i18n path="admin.icon_disclaimer">
                    <a href="https://fontawesome.com/search?ip=classic&s=light&o=r" target="_blank">
                        {{ $t('admin.icon_disclaimer_link') }}
                    </a>
                </i18n>
            </b-alert>
            <form id="categoryForm" @submit.prevent="submit">
                <label for="name">{{ $t('admin.category_modal_name') }}</label>
                <b-form-input
                    id="name"
                    v-model="name"
                    :disabled="isEdit"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.category_modal_name_placeholder')"
                />
                <label for="translation">{{ $t('admin.category_modal_translation') }}</label>
                <b-form-input
                    id="translation"
                    v-model="translation"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.category_modal_translation_placeholder')"
                />
                <label for="signBankIds">{{ $t('admin.category_modal_signbankids') }}</label>
                <b-form-input
                    id="signBankIds"
                    v-model="signBankIdsString"
                    type="text"
                    class="mt-2 mb-2"
                    @focus="error = null"
                    :placeholder="$t('admin.category_modal_signbankids_placeholder')"
                />
                <small class="text-muted mb-4 d-block">{{ $t('admin.category_modal_signbankids_help') }}</small>
                <label for="icon">{{ $t('admin.category_modal_icon') }}</label>
                <b-form-input
                    id="icon"
                    v-model="icon"
                    type="text"
                    class="mt-2 mb-4"
                    @focus="error = null"
                    :placeholder="$t('admin.category_modal_icon_placeholder')"
                />
                <IconPreview :icon="icon" :translation="translation" />
                <b-alert v-if="error" variant="error" show>
                    <font-awesome-icon :icon="['far', 'square-exclamation']" size="xl" class="mr-2"></font-awesome-icon>
                    {{ error }}
                </b-alert>
            </form>
        </template>
        <template v-slot:modal-footer>
            <div class="d-flex">
                <button type="submit" form="categoryForm" class="vgtc-button mr-4">
                    {{ isEdit ? $t('general.edit') : $t('general.create') }}
                </button>
                <button @click="hideModal" class="vgtc-button vgtc-button--invert">
                    {{ $t('general.cancel') }}
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Constants } from '@/shared/Constants';
import { axiosHttp } from '@/util/AxiosHttp';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { ICategory } from '../../../../functions/src/shared/contracts/api/MasterdataContract';
import IconPreview from './IconPreview.vue';

@Component({ components: { IconPreview } })
export default class CategoryModal extends Vue {
    public name: string = '';
    public translation: string = '';
    public signBankIdsString: string = '';
    public icon: string = '';

    @Prop() readonly category!: ICategory;

    public error: string | null = null;

    get signBankIds(): number[] {
        if (!this.signBankIdsString.trim()) return [];
        return this.signBankIdsString.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    }

    set signBankIds(value: number[]) {
        this.signBankIdsString = value.join(', ');
    }

    @Watch('category', { immediate: true })
    public init() {
        if (this.category && this.category.id) {
            // Editing existing category
            this.name = this.category.id;
            this.translation = this.category.name;
            this.signBankIds = this.category.signBankIds || [];
            this.icon = this.category.icon || '';
        } else {
            // Creating new category - ensure fields are empty
            this.name = '';
            this.translation = '';
            this.signBankIdsString = '';
            this.icon = '';
        }
        this.error = null;
    }

    public resetFields() {
        this.name = '';
        this.translation = '';
        this.signBankIdsString = '';
        this.icon = '';
        this.error = null;
    }

    get isEdit(): boolean {
        return this.category && this.category.id !== '';
    }

    public async createCategory() {
        try {
            this.error = '';
            await axiosHttp.http.post(`${Constants.baseOperationsUrl}/categories`, {
                name: this.name,
                translation: this.translation,
                signBankIds: this.signBankIds,
                icon: this.icon,
            });
            this.hideModal();
        } catch (error) {
            this.error = `${this.$t('admin.category_creation_error')}: ${error}`;
        }
    }

    public async editCategory(category: ICategory) {
        this.error = null;

        try {
            await axiosHttp.http.put<ICategory>(`${Constants.baseOperationsUrl}/categories/${category.id}`, {
                id: this.name,
                translation: this.translation,
                signBankIds: this.signBankIds,
                icon: this.icon,
            });

            this.hideModal();
        } catch (error) {
            this.error = `${this.$t('admin.category_modal_error_update_failed')}: ${error}`;
        }
    }

    public showModal() {
        this.$bvModal.show('categoryModal');
    }

    public hideModal() {
        this.$bvModal.hide('categoryModal');
    }

    @Emit()
    public async submit() {
        if (this.isEdit) {
            await this.editCategory(this.category);
        } else {
            await this.createCategory();
        }
    }
}
</script>
